import { Text } from '@radix-ui/themes';
import clsx from 'clsx';
import HubertCombobox from 'components/HubertUI/HubertCombobox';
import { HubertSlider } from 'components/HubertUI/HubertSlider';
import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EEvaluationType, TEvaluation, TSliderEvaluationConditional } from 'types/evaluation.types';
import type { TSelectBase } from 'types/general.types';
import { EConditionalOperators } from '@hubertai/screening-question-types';
import type { TFormSlider } from '@hubertai/screening-question-types';
import EvaluationPopover from '..';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';
import { useAppSelector } from 'rtk/query/hooks';
import { selectQuestionLanguage } from 'rtk/questionEditorSelector';

interface Props extends ComponentProps<'div'> {
  form: TFormSlider;
  item: TSliderEvaluationConditional;
  onUpdateItem: (updateField: string, value: string | string[] | number[] | TEvaluation) => void;
  children: ReactNode;
}

const getValidConditionValue = (item: TSliderEvaluationConditional, min: number, max: number) => {
  const conditionValue = item.condition.value;
  const operator = item.condition.operator;
  const updateValue: [number] = [0];

  if (operator === EConditionalOperators.Between) {
    if (!conditionValue) {
      return [min, max];
    }

    if (Array.isArray(conditionValue) && conditionValue.length) {
      if (conditionValue.length === 1) {
        return [conditionValue[0], max];
      } else {
        return [conditionValue[0], conditionValue[1]];
      }
    }

    if (typeof conditionValue === 'number') {
      return [conditionValue, max];
    }

    return [min, max];
  } else {
    if (!conditionValue) {
      return [min];
    }

    if (Array.isArray(conditionValue) && conditionValue.length) {
      return [conditionValue[0]];
    }

    if (typeof conditionValue === 'number') {
      return [conditionValue];
    }
  }

  return updateValue;
};

const SliderEvaluationConditionalItemv2 = ({ form, item, onUpdateItem, children, ...props }: Props) => {
  const { t } = useTranslation('Templates');
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));

  const sliderEvaluationOperatorList = useMemo(
    () => [
      { label: t('evaluationConditional.operators.lessThan'), value: 'LESS_THAN' },
      { label: t('evaluationConditional.operators.lessThanOrEqualTo'), value: 'LESS_THAN_EQUAL_TO' },
      { label: t('evaluationConditional.operators.equals'), value: 'EQUALS' },
      { label: t('evaluationConditional.operators.greaterThanOrEqualTo'), value: 'GREATER_THAN_EQUAL_TO' },
      { label: t('evaluationConditional.operators.greaterThan'), value: 'GREATER_THAN' },
      { label: t('evaluationConditional.operators.between'), value: 'BETWEEN' },
    ],
    [t],
  );

  const [_, setOperator] = useState<TSelectBase>(
    // @ts-ignore
    sliderEvaluationOperatorList.find((x) => x.value === item.condition.operator),
  );

  useEffect(() => {
    setOperator(
      // @ts-ignore
      sliderEvaluationOperatorList.find((x) => x.value === item.condition.operator),
    );

    const updateValue = getValidConditionValue(item, form.min[questionLanguage] ?? 0, form.max[questionLanguage] ?? 10);
    onUpdateItem('condition-value', updateValue);
  }, [item.condition.operator]);

  const [evaluation, setEvaluation] = useState<{
    name: EEvaluationType;
    bonus_points?: number;
  }>({ name: item.name, bonus_points: item.bonus_points });

  return (
    <div
      {...props}
      className={clsx('flex flex-col justify-between gap-4', 'my-2', 'rounded-md border', 'hubert-vertical-center')}
    >
      {children}

      <div className={clsx('flex flex-row justify-between gap-4', 'p-2', 'w-full', 'hubert-vertical-center')}>
        <div className={clsx('flex flex-row justify-start', 'w-full', 'hubert-vertical-center')}>
          <HubertCombobox
            isMulti={false}
            className={clsx('w-full', 'text-xs')}
            value={item.condition && item.condition.operator ? item.condition.operator : undefined}
            itemName={t('evaluationConditional.operatorItemName')}
            items={sliderEvaluationOperatorList}
            onValueChange={(e) => {
              if (onUpdateItem) {
                onUpdateItem('condition-operator', e);
              }
            }}
          />
        </div>

        <div className={clsx('flex flex-row justify-end gap-2', 'w-full', 'hubert-vertical-center')}>
          <Text>
            {(() => {
              if (!item || !item.condition || !item.condition.value) {
                return <div>{t('evaluationConditional.erroneousConditionValue')}</div>;
              }

              if (typeof item.condition.value === 'string') {
                return item.condition.value;
              } else if (typeof item.condition.value === 'number') {
                return item.condition.value;
              } else if (Array.isArray(item.condition.value) && item.condition.value.length) {
                console.log('item.condition.value isArray', item.condition.value);
                return item.condition.value[0];
              } else {
                return <div>{t('evaluationConditional.erroneousConditionValue2')}</div>;
              }
            })()}
          </Text>

          <HubertSlider
            value={Array.isArray(item.condition.value) ? item.condition.value : [item.condition.value]}
            min={form.min[questionLanguage] ?? 0}
            max={form.max[questionLanguage] ?? 100}
            step={form.step[questionLanguage] ?? 1}
            onValueChange={(e) => {
              if (onUpdateItem) {
                onUpdateItem('condition-value', e);
              }
            }}
            onValueCommit={(e: number[]) => {
              if (onUpdateItem) {
                onUpdateItem('condition-value', e);
              }
            }}
          />

          <Text>
            {(() => {
              if (Array.isArray(item.condition.value) && item.condition.value.length === 2) {
                return item.condition.value[1];
              } else {
                return null;
              }
            })()}
          </Text>
        </div>
      </div>

      <div className={clsx('flex flex-row justify-center gap-4', 'p-2', 'w-full', 'hubert-vertical-center')}>
        <Text>{t('evaluationConditional.thenEvaluateAs')}</Text>

        {/* @ts-ignore */}
        <EvaluationPopover
          allowedTabs={['info', 'warning', 'bonus', 'disqualified']}
          nodeId=""
          customKey=""
          evaluation={evaluation}
          onEvaluationChange={(e) => {
            setEvaluation(e);
            if (onUpdateItem) {
              onUpdateItem('evaluation', e);
            }
          }}
        />
      </div>
    </div>
  );
};

export default SliderEvaluationConditionalItemv2;
